import { apiClient } from '../client';

/**
 * Client for the Rock assistant (Phase H — Rocks workflow/AI).
 *
 * A single endpoint — `POST /api/eos/rock-assistant` — drafts a rock from an
 * optional seat + free-text note. The server is deliberately terse: it either
 * returns a suggestion or one of a handful of `{ error }` envelopes. The 503
 * `ai_disabled` case (AI not configured for the org) is surfaced as a typed
 * {@link AiDisabledError} so the UI can quietly degrade the assist button
 * rather than nag with a toast.
 */

export interface RockSuggestion {
  title: string;
  description: string;
  milestones: { title: string }[];
}

export interface SuggestRockRequest {
  seatId?: string;
  note?: string;
}

/** Thrown when the server reports AI is not configured (503 `ai_disabled`). */
export class AiDisabledError extends Error {
  constructor() {
    super('AI assistance is not configured for this organization.');
    this.name = 'AiDisabledError';
  }
}

/** True when an error is the 503 `ai_disabled` envelope. */
function isAiDisabled(err: unknown): boolean {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number; data?: { error?: unknown } } }).response;
    return response?.status === 503 && response.data?.error === 'ai_disabled';
  }
  return false;
}

/**
 * Ask the assistant to draft a rock. Resolves with the suggestion, throws
 * {@link AiDisabledError} when AI is switched off, and otherwise re-throws the
 * axios error (carrying `ai_refused` / `ai_unavailable` / `seat_not_found`)
 * for the caller to map to a toast.
 */
export async function suggestRock(body: SuggestRockRequest = {}): Promise<RockSuggestion> {
  try {
    const res = await apiClient.post<{ data: RockSuggestion }>('/api/eos/rock-assistant', body);
    return res.data.data;
  } catch (err) {
    if (isAiDisabled(err)) throw new AiDisabledError();
    throw err;
  }
}
